import React, { useEffect, useRef, useState } from "react";
import { Globe, Handshake, Link2, Smartphone } from "lucide-react";

const stats = [
  { value: "1994", label: "Zara FM'in kuruluşu" },
  { value: "1997", label: "Sivas Zara Haber'in kuruluşu" },
  { value: "1.5M+", label: "Türkiye'deki Sivaslı" },
  { value: "3.5M+", label: "Dünya genelindeki Sivaslı" },
];

const milestones = [
  {
    year: "1994",
    title: "Zara FM",
    desc: "Sivas'ın sesi olarak yayın hayatına başladı.",
  },
  {
    year: "1997",
    title: "Sivas Zara Haber",
    desc: "Basılı dergi olarak dünyanın dört bir yanındaki hemşehrilerimize ulaştı.",
  },
  {
    year: "2020",
    title: "Dijital Dönüşüm",
    desc: "Dijital ve sosyal medya mecralarında yayın hayatını sürdürmeye başladı.",
  },
  {
    year: "2025",
    title: "istesivas.com",
    desc: "Otuz yılı aşan tecrübenin dijital ortamdaki yeni nesil buluşma noktası.",
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function HakkimizdaPage() {
  return (
    <div className="min-h-screen bg-[#f8f5f0] font-serif">
      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-[#1a2e3b] min-h-[52vh] flex items-center">
        {/* Dekoratif daireler */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-amber-400/5 blur-2xl pointer-events-none" />
        {/* Grid desen */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20">
          <div
            style={{
              opacity: 1,
              animation: "fadeSlideUp 0.9s ease both",
            }}
          >
            <span className="inline-block text-amber-400 text-xs font-sans font-bold tracking-[0.25em] uppercase mb-5 border border-amber-400/30 px-3 py-1 rounded-full">
              1994'ten Bugüne
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
              Sivaslıları
              <br />
              <span className="text-amber-400">birbirine bağlıyoruz.</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-2xl leading-relaxed font-sans font-light">
              Otuz yılı aşan bir yayıncılık birikiminin dijital ortamdaki yeni
              nesil buluşma noktası.
            </p>
          </div>
        </div>

        {/* Alt eğri */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
          >
            <path d="M0 60L1440 0V60H0Z" fill="#f8f5f0" />
          </svg>
        </div>
      </section>

      {/* ── İSTATİSTİKLER ── */}
      <section className="max-w-5xl mx-auto px-6 -mt-2 mb-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <FadeIn key={s.value} delay={i * 100}>
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-stone-200 hover:shadow-md transition-shadow">
                <div className="text-3xl md:text-4xl font-bold text-[#1a2e3b] mb-1">
                  {s.value}
                </div>
                <div className="text-xs text-stone-500 font-sans leading-tight">
                  {s.label}
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── KÖKEN YAZISI ── */}
      <section className="max-w-4xl mx-auto px-6 mb-20">
        <FadeIn>
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-stone-200 relative overflow-hidden">
            {/* Dekor */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-amber-50 rounded-bl-full opacity-60 pointer-events-none" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-amber-400 flex items-center justify-center shrink-0">
                  <svg
                    className="w-5 h-5 text-[#1a2e3b]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                  </svg>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#1a2e3b]">
                  Kökenimiz
                </h2>
              </div>
              <p className="text-stone-600 leading-8 text-base md:text-lg font-sans">
                <strong className="text-[#1a2e3b]">istesivas.com</strong>,
                kökleri 1994 yılında yayın hayatına başlayan{" "}
                <span className="text-amber-600 font-semibold">Zara FM</span>'e,
                1997 yılında kurulan ve uzun yıllar boyunca Sivas'ın en etkili
                yerel yayın organlarından biri olarak faaliyet gösteren{" "}
                <span className="text-amber-600 font-semibold">
                  Sivas Zara Haber
                </span>
                'e dayanan güçlü bir birikimin ürünüdür.
              </p>
              <p className="text-stone-600 leading-8 text-base md:text-lg font-sans mt-4">
                Sivas Zara Haber, 1997 yılından 2020 yılına kadar dergi kimliği
                ile basılı yayın olarak dünyanın bir çok yerindeki hemşehrimize
                ulaşmış; kültürel, sosyal ve ekonomik alanda Sivaslılar arasında
                güçlü bağların kurulmasına katkı sağlamıştır. 2020 yılından
                itibaren ise dijital ve sosyal medya mecralarında yayın hayatını
                sürdürerek Sivas'ın sesi olmaya devam etmiştir.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── TARİHSEL ZAMAN ÇİZGİSİ ── */}
      <section className="max-w-4xl mx-auto px-6 mb-20">
        <FadeIn>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1a2e3b] mb-10 text-center">
            Yolculuğumuz
          </h2>
        </FadeIn>
        <div className="relative">
          {/* Dikey çizgi */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-amber-200 md:-translate-x-px" />

          <div className="space-y-8">
            {milestones.map((m, i) => (
              <FadeIn key={m.year} delay={i * 120}>
                <div
                  className={`relative flex items-start gap-6 md:gap-0 ${
                    i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  {/* Nokta */}
                  <div className="absolute left-6 md:left-1/2 w-4 h-4 rounded-full bg-amber-400 border-4 border-[#f8f5f0] -translate-x-1.5 md:-translate-x-2 mt-1 z-10 shrink-0" />

                  {/* Kart */}
                  <div
                    className={`ml-14 md:ml-0 md:w-[calc(50%-2rem)] ${
                      i % 2 === 0 ? "md:mr-auto md:pr-8" : "md:ml-auto md:pl-8"
                    }`}
                  >
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-stone-200 hover:shadow-md transition-shadow">
                      <span className="text-amber-500 text-xs font-sans font-bold tracking-widest uppercase">
                        {m.year}
                      </span>
                      <h3 className="text-[#1a2e3b] font-bold text-lg mt-1 mb-1">
                        {m.title}
                      </h3>
                      <p className="text-stone-500 text-sm font-sans leading-relaxed">
                        {m.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── MİSYON ── */}
      <section className="bg-[#1a2e3b] relative overflow-hidden py-20 mb-0">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="inline-block text-amber-400 text-xs font-sans font-bold tracking-[0.2em] uppercase mb-4 border border-amber-400/30 px-3 py-1 rounded-full">
                Misyonumuz
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Neden istesivas.com?
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: <Globe className="w-7 h-7 text-amber-400" />,
                title: "3.5 Milyon Sivaslı",
                body: "Türkiye'nin farklı şehirlerinde yaklaşık 1,5 milyon, dünyanın çeşitli ülkelerinde ise 3,5 milyona yakın Sivaslı yaşamaktadır.",
              },
              {
                icon: <Handshake className="w-7 h-7 text-amber-400" />,
                title: "Hemşehrilik Bağı",
                body: "Bu büyük nüfusa rağmen hemşehrilerimizin birbirlerine ulaşmaları, ticari ilişkiler kurmaları her zaman kolay olmamaktadır.",
              },
              {
                icon: <Link2 className="w-7 h-7 text-amber-400" />,
                title: "Tek Platform",
                body: "Bir usta, avukat, doktor, esnaf arayan vatandaşlarımız, tek bir platform üzerinden hemşehrilerine hatta kendi köylüsüne kadar ulaşabilmektedir.",
              },
              {
                icon: <Smartphone className="w-7 h-7 text-amber-400" />,
                title: "Mobil Uygulama",
                body: "iOS ve Android mobil uygulamalarımız sayesinde kullanıcılarımız hemşehrileriyle daha hızlı ve kolay şekilde iletişim kurabilmektedir.",
              },
            ].map((card, i) => (
              <FadeIn key={card.title} delay={i * 100}>
                <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="mb-3">{card.icon}</div>
                  <h3 className="text-white font-bold text-lg mb-2">
                    {card.title}
                  </h3>
                  <p className="text-slate-300 text-sm font-sans leading-relaxed">
                    {card.body}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── UZUN METİN BÖLÜMÜ ── */}
      <section className="max-w-3xl mx-auto px-6 py-20">
        <FadeIn>
          <div className="space-y-6 text-stone-700 font-sans text-base md:text-lg leading-8">
            <p>
              <strong className="text-[#1a2e3b]">istesivas.com</strong>,
              yaklaşık otuz yılı aşan bu tecrübenin, değişen dünyanın
              ihtiyaçları doğrultusunda dijital ortama taşınmış yeni nesil bir
              buluşma noktasıdır.
            </p>
            <p>
              Platformumuzun temel hedefi; dünyanın neresinde olursa olsun bir
              Sivaslının ihtiyaç duyduğu hizmete{" "}
              <span className="text-amber-600 font-semibold">
                öncelikle kendi hemşehrileri aracılığıyla
              </span>{" "}
              ulaşabilmesini sağlamaktır.
            </p>
            <div className="border-l-4 border-amber-400 pl-6 py-2 bg-amber-50 rounded-r-xl">
              <p className="text-[#1a2e3b] font-medium italic">
                "Ancak istesivas.com yalnızca bir firma rehberi veya ilan
                platformu değildir. Bu proje; dayanışmayı güçlendiren,
                hemşehrilik bağlarını pekiştiren, sosyal ilişkileri ekonomik iş
                birliklerine dönüştüren bir{" "}
                <strong>toplumsal kalkınma projesidir.</strong>"
              </p>
            </div>
            <p>
              Çünkü inanıyoruz ki güçlü toplumlar, birbirini tanıyan, birbirine
              güvenen ve birbirini destekleyen insanlardan oluşur.
            </p>
            <p>
              Sisteme kayıt tamamen{" "}
              <span className="text-amber-600 font-semibold">ücretsizdir</span>.
              Ticaret yapan, hizmet üreten veya mesleki faaliyet gösteren tüm
              hemşehrilerimiz kendilerini tanıtabilir, daha geniş kitlelere
              ulaşabilir ve yeni iş bağlantıları kurabilirler.
            </p>
            <p>
              Dünyanın bir çok ülkesinde, farklı gruplar amaçları farklı olsa da
              benzer modeller uygulayarak hiç kimsenin tahmin etmediği ciddi
              ekonomik topluluklar oluşturmuşlardır. Bu platform; Sivaslıların
              birbirini bulduğu, tanıdığı, desteklediği ve birlikte büyüdüğü
              güçlü bir dijital ekosistemin ilk adımıdır.
            </p>
          </div>
        </FadeIn>
      </section>

      {/* ── DAVET CTA ── */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <FadeIn>
          <div className="bg-amber-400 rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-amber-300/40 pointer-events-none" />
            <div className="absolute -bottom-10 -left-10 w-64 h-64 rounded-full bg-amber-500/20 pointer-events-none" />
            <div className="relative z-10">
              <h2 className="text-2xl md:text-4xl font-bold text-[#1a2e3b] mb-4 leading-tight">
                Geçmişten aldığımız güçle,
                <br />
                geleceğe uzanan yolculuk.
              </h2>
              <p className="text-[#1a2e3b]/70 font-sans mb-6 max-w-xl mx-auto">
                Tüm hemşehrilerimizi istesivas.com ailesinin bir parçası olmaya
                davet ediyoruz.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
